import React, { useState } from 'react';
import { Plus, Upload, X, Eye, Trash2, Edit, Users, GripVertical, Save } from 'lucide-react';
import * as XLSX from 'xlsx';

const CustomizableForms = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [formType, setFormType] = useState(''); // 'new' or 'prebuilt'
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showFormBuilder, setShowFormBuilder] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [excelPreviewData, setExcelPreviewData] = useState(null);
  const [editingFormId, setEditingFormId] = useState(null);
  
  // Form builder states
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formRecurrence, setFormRecurrence] = useState('one-time');
  const [formFields, setFormFields] = useState([]);
  const [fieldType, setFieldType] = useState('text');
  
  // Assignment states
  const [selectedDistricts, setSelectedDistricts] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);

  const districts = [
    'All Districts',
    'Chennai',
    'Coimbatore',
    'Madurai',
    'Tiruchirappalli',
    'Salem',
    'Tirunelveli',
    'Vellore',
    'Erode',
    'Thanjavur',
    'Kanyakumari'
  ];

  const roles = [
    'Section Officer',
    'Police Inspector',
    'DADWO Officer',
    'Warden'
  ];

  const fieldTypes = [
    { value: 'text', label: 'Text Input' },
    { value: 'textarea', label: 'Paragraph' },
    { value: 'number', label: 'Number' },
    { value: 'email', label: 'Email' },
    { value: 'date', label: 'Date' },
    { value: 'dropdown', label: 'Dropdown' },
    { value: 'radio', label: 'Radio Button' },
    { value: 'checkbox', label: 'Checkbox' },
  ];

  const masterOptions = [
    { value: 'districts', label: 'Districts Master' },
    { value: 'taluks', label: 'Taluks Master' },
    { value: 'villages', label: 'Villages Master' },
    { value: 'departments', label: 'Departments Master' },
    { value: 'designations', label: 'Designations Master' },
    { value: 'categories', label: 'Categories Master' },
    { value: 'schemes', label: 'Schemes Master' },
    { value: 'status', label: 'Status Master' }
  ];

  const [forms, setForms] = useState([
    { 
      id: 1, 
      name: 'Test Form Alpha', 
      type: 'New Form', 
      createdDate: '2024-01-15', 
      status: 'Active', 
      responses: 45,
      description: 'Sample form for testing',
      recurrence: 'one-time',
      fields: [
        { id: 1, sno: 1, label: 'Full Name', type: 'text', required: true, masterData: '' },
        { id: 2, sno: 2, label: 'Email', type: 'email', required: true, masterData: '' },
        { id: 3, sno: 3, label: 'District', type: 'dropdown', required: true, masterData: 'districts' }
      ]
    },
    { 
      id: 2, 
      name: 'Test Form Beta', 
      type: 'Pre-built', 
      createdDate: '2024-01-14', 
      status: 'Active', 
      responses: 23,
      description: 'Pre-built form',
      recurrence: 'weekly',
      fields: [
        { id: 4, sno: 1, label: 'Department', type: 'dropdown', required: true, masterData: 'departments' },
        { id: 5, sno: 2, label: 'Remarks', type: 'textarea', required: false, masterData: '' }
      ]
    },
    { 
      id: 3, 
      name: 'Test Form Gamma', 
      type: 'New Form', 
      createdDate: '2024-01-13', 
      status: 'Draft', 
      responses: 0,
      description: 'Draft form',
      recurrence: 'monthly',
      fields: []
    },
  ]);

  const handleCreateFormClick = () => {
    setShowTypeModal(true);
  };

  const handleTypeSelection = (type) => {
    setFormType(type);
    setShowTypeModal(false);
    if (type === 'new') {
      setEditingFormId(null);
      setShowFormBuilder(true);
      setFormName('');
      setFormDescription('');
      setFormRecurrence('one-time');
      setFormFields([]);
    } else {
      document.getElementById('excel-upload').click();
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFile(file);
      
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          if (jsonData.length > 0) {
            const headers = jsonData[0];
            const rows = jsonData.slice(1, 11);
            
            setExcelPreviewData({
              fileName: file.name,
              headers: headers,
              rows: rows
            });
            setShowPreviewModal(true);
          } else {
            alert('The Excel file appears to be empty');
          }
        } catch (error) {
          console.error('Error reading Excel file:', error);
          alert('Error reading Excel file. Please make sure it is a valid Excel file.');
        }
      };
      
      reader.onerror = () => {
        alert('Error reading file');
      };
      
      reader.readAsArrayBuffer(file);
    }
    
    e.target.value = '';
  };

  const addField = () => {
    const newField = {
      id: Date.now(),
      sno: formFields.length + 1,
      label: '',
      type: fieldType,
      required: false,
      masterData: '',
      options: fieldType === 'dropdown' || fieldType === 'radio' ? ['Option 1', 'Option 2'] : []
    };
    setFormFields([...formFields, newField]);
  };

  const updateField = (id, key, value) => {
    setFormFields(formFields.map(field => 
      field.id === id ? { ...field, [key]: value } : field
    ));
  };

  const removeField = (id) => {
    const updatedFields = formFields
      .filter(field => field.id !== id)
      .map((field, index) => ({ ...field, sno: index + 1 }));
    setFormFields(updatedFields);
  };

  // NEW: Save as Draft
  const handleSaveAsDraft = () => {
    if (!formName) {
      alert('Please enter a form name');
      return;
    }

    if (editingFormId) {
      // Update existing form as draft
      setForms(forms.map(form => 
        form.id === editingFormId 
          ? { 
              ...form, 
              name: formName, 
              description: formDescription, 
              recurrence: formRecurrence,
              fields: formFields,
              status: 'Draft'
            }
          : form
      ));
      alert('Form updated and saved as draft!');
    } else {
      // Create new form as draft
      const newForm = {
        id: Date.now(),
        name: formName,
        type: 'New Form',
        createdDate: new Date().toISOString().split('T')[0],
        status: 'Draft',
        responses: 0,
        description: formDescription,
        recurrence: formRecurrence,
        fields: formFields
      };
      setForms([...forms, newForm]);
      alert('Form saved as draft!');
    }
    
    setShowFormBuilder(false);
    setEditingFormId(null);
  };

  const handlePublishForm = () => {
    if (editingFormId) {
      // Update existing form
      setForms(forms.map(form => 
        form.id === editingFormId 
          ? { 
              ...form, 
              name: formName, 
              description: formDescription, 
              recurrence: formRecurrence,
              fields: formFields,
              status: 'Active'
            }
          : form
      ));
    } else {
      // Create new form
      const newForm = {
        id: Date.now(),
        name: formName,
        type: 'New Form',
        createdDate: new Date().toISOString().split('T')[0],
        status: 'Active',
        responses: 0,
        description: formDescription,
        recurrence: formRecurrence,
        fields: formFields
      };
      setForms([...forms, newForm]);
    }
    
    setShowFormBuilder(false);
    setShowAssignModal(true);
  };

  const handleDistrictChange = (district) => {
    if (district === 'All Districts') {
      setSelectedDistricts(['All Districts']);
    } else {
      const filtered = selectedDistricts.filter(d => d !== 'All Districts');
      if (filtered.includes(district)) {
        setSelectedDistricts(filtered.filter(d => d !== district));
      } else {
        setSelectedDistricts([...filtered, district]);
      }
    }
  };

  const handleRoleToggle = (role) => {
    if (selectedRoles.includes(role)) {
      setSelectedRoles(selectedRoles.filter(r => r !== role));
    } else {
      setSelectedRoles([...selectedRoles, role]);
    }
  };

  const handleAssignClick = (form) => {
    setSelectedForm(form);
    setSelectedDistricts([]);
    setSelectedRoles([]);
    setShowAssignModal(true);
  };

  const handleViewClick = (form) => {
    setSelectedForm(form);
    setShowViewModal(true);
  };

  const handleEditClick = (form) => {
    setEditingFormId(form.id);
    setFormName(form.name);
    setFormDescription(form.description);
    setFormRecurrence(form.recurrence);
    setFormFields(form.fields);
    setShowFormBuilder(true);
  };

  const handleDeleteClick = (formId) => {
    if (window.confirm('Are you sure you want to delete this form?')) {
      setForms(forms.filter(form => form.id !== formId));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Customizable Forms</h1>
          <p className="text-sm text-gray-600 mt-1">Create and manage custom forms</p>
        </div>
        <button
          onClick={handleCreateFormClick}
          className="px-4 py-2 bg-[#3d82c4] text-white font-medium hover:bg-[#2d6fad] transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Create Form
        </button>
      </div>

      {/* Forms Table */}
      <div className="bg-white border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Form Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Created Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Responses
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {forms.map((form) => (
                <tr key={form.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{form.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-700">{form.type}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-700">{form.createdDate}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 text-xs font-semibold ${
                      form.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {form.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-700">{form.responses}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleAssignClick(form)}
                        className="p-2 text-blue-600 hover:bg-blue-50 transition-colors"
                        title="Assign to Users"
                      >
                        <Users className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleViewClick(form)}
                        className="p-2 text-green-600 hover:bg-green-50 transition-colors" 
                        title="Preview"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleEditClick(form)}
                        className="p-2 text-orange-600 hover:bg-orange-50 transition-colors" 
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleDeleteClick(form.id)}
                        className="p-2 text-red-600 hover:bg-red-50 transition-colors" 
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Hidden File Input */}
      <input
        type="file"
        id="excel-upload"
        accept=".xlsx,.xls"
        className="hidden"
        onChange={handleFileUpload}
      />

      {/* Type Selection Modal */}
      {showTypeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-800">Select Form Type</h2>
            </div>
            <div className="p-6 space-y-4">
              <button
                onClick={() => handleTypeSelection('new')}
                className="w-full p-4 border-2 border-gray-200 hover:border-[#3d82c4] hover:bg-blue-50 transition-all text-left"
              >
                <h3 className="font-semibold text-gray-800 mb-1">New Form</h3>
                <p className="text-sm text-gray-600">Build a form from scratch using form builder</p>
              </button>
              <button
                onClick={() => handleTypeSelection('prebuilt')}
                className="w-full p-4 border-2 border-gray-200 hover:border-[#3d82c4] hover:bg-blue-50 transition-all text-left"
              >
                <h3 className="font-semibold text-gray-800 mb-1">Pre-built Form</h3>
                <p className="text-sm text-gray-600">Upload an Excel sheet to create form</p>
              </button>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowTypeModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Form Builder Modal */}
      {showFormBuilder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-800">
                {editingFormId ? 'Edit Form' : 'Build New Form'}
              </h2>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Form Info */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Form Name</label>
                  <input
                    type="text"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    placeholder="Enter form name"
                    className="w-full px-4 py-2 border border-gray-300 focus:outline-none focus:border-[#3d82c4]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                  <textarea
                    value={formDescription}
                    onChange={(e) => setFormDescription(e.target.value)}
                    placeholder="Enter form description"
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 focus:outline-none focus:border-[#3d82c4]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Form Recurrence</label>
                  <div className="flex flex-wrap gap-4">
                    {['one-time', 'weekly', 'monthly', 'quarterly'].map((type) => (
                      <label key={type} className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          value={type}
                          checked={formRecurrence === type}
                          onChange={(e) => setFormRecurrence(e.target.value)}
                          className="mr-2 w-4 h-4 text-[#3d82c4]"
                        />
                        <span className="text-sm font-medium text-gray-700 capitalize">
                          {type.replace('-', ' ')}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Add Field Section */}
              <div className="border-t pt-6">
                <div className="flex items-center gap-4 mb-4">
                  <select
                    value={fieldType}
                    onChange={(e) => setFieldType(e.target.value)}
                    className="px-4 py-2 border border-gray-300 focus:outline-none focus:border-[#3d82c4]"
                  >
                    {fieldTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                  <button
                    onClick={addField}
                    className="px-4 py-2 bg-[#3d82c4] text-white font-medium hover:bg-[#2d6fad] transition-colors flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add Field
                  </button>
                </div>

                {/* Form Fields List */}
                <div className="space-y-4">
                  {formFields.map((field, index) => (
                    <div key={field.id} className="border border-gray-200 p-4 bg-gray-50">
                      <div className="flex items-start gap-4">
                        <div className="flex flex-col items-center gap-1">
                          <span className="text-lg font-bold text-[#3d82c4]">{field.sno}.</span>
                          <GripVertical className="w-5 h-5 text-gray-400 cursor-move" />
                        </div>
                        <div className="flex-1 space-y-3">
                          <div className="flex gap-4">
                            <input
                              type="text"
                              value={field.label}
                              onChange={(e) => updateField(field.id, 'label', e.target.value)}
                              placeholder="Field Label"
                              className="flex-1 px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#3d82c4]"
                            />
                            <select
                              value={field.type}
                              onChange={(e) => updateField(field.id, 'type', e.target.value)}
                              className="px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#3d82c4]"
                            >
                              {fieldTypes.map(type => (
                                <option key={type.value} value={type.value}>{type.label}</option>
                              ))}
                            </select>
                          </div>
                          {field.type === 'dropdown' && (
                            <div>
                              <label className="block text-xs font-semibold text-gray-600 mb-1">
                                Select Master Data
                              </label>
                              <select
                                value={field.masterData}
                                onChange={(e) => updateField(field.id, 'masterData', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#3d82c4]"
                              >
                                <option value="">Select Master</option>
                                {masterOptions.map((master) => (
                                  <option key={master.value} value={master.value}>
                                    {master.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={field.required}
                              onChange={(e) => updateField(field.id, 'required', e.target.checked)}
                              className="w-4 h-4 text-[#3d82c4] border-gray-300"
                            />
                            <span className="text-sm text-gray-700">Required field</span>
                          </label>
                        </div>
                        <button
                          onClick={() => removeField(field.id)}
                          className="p-2 text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                  {formFields.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-8">No fields added yet. Click "Add Field" to start building your form.</p>
                  )}
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
              <button
                onClick={() => {
                  setShowFormBuilder(false);
                  setEditingFormId(null);
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              
              <div className="flex gap-3">
                {/* NEW: Save as Draft Button */}
                <button
                  onClick={handleSaveAsDraft}
                  disabled={!formName}
                  className="px-4 py-2 bg-gray-500 text-white font-medium hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Save as Draft
                </button>

                <button
                  onClick={handlePublishForm}
                  disabled={!formName || formFields.length === 0}
                  className="px-4 py-2 bg-[#3d82c4] text-white font-medium hover:bg-[#2d6fad] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {editingFormId ? 'Update & Publish' : 'Publish & Assign'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Form Modal */}
      {showViewModal && selectedForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-800">{selectedForm.name}</h2>
                <p className="text-sm text-gray-600 mt-1">{selectedForm.description}</p>
              </div>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200">
                <p className="text-sm text-gray-700">
                  <strong>Recurrence:</strong> <span className="capitalize">{selectedForm.recurrence.replace('-', ' ')}</span>
                </p>
                <p className="text-sm text-gray-700 mt-1">
                  <strong>Status:</strong> {selectedForm.status}
                </p>
              </div>

              <h3 className="text-lg font-semibold text-gray-800 mb-4">Form Preview</h3>
              <div className="space-y-4">
                {selectedForm.fields.map((field) => (
                  <div key={field.id} className="border border-gray-200 p-4 bg-gray-50">
                    <div className="flex items-start gap-3">
                      <span className="text-lg font-bold text-[#3d82c4]">{field.sno}.</span>
                      <div className="flex-1">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          {field.label}
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                          {field.masterData && (
                            <span className="ml-2 text-xs text-blue-600">
                              ({masterOptions.find(m => m.value === field.masterData)?.label})
                            </span>
                          )}
                        </label>
                        {field.type === 'text' && (
                          <input
                            type="text"
                            disabled
                            placeholder="Text input"
                            className="w-full px-3 py-2 border border-gray-300 bg-white"
                          />
                        )}
                        {field.type === 'textarea' && (
                          <textarea
                            disabled
                            placeholder="Paragraph"
                            rows="3"
                            className="w-full px-3 py-2 border border-gray-300 bg-white"
                          />
                        )}
                        {field.type === 'number' && (
                          <input
                            type="number"
                            disabled
                            placeholder="Number input"
                            className="w-full px-3 py-2 border border-gray-300 bg-white"
                          />
                        )}
                        {field.type === 'email' && (
                          <input
                            type="email"
                            disabled
                            placeholder="Email input"
                            className="w-full px-3 py-2 border border-gray-300 bg-white"
                          />
                        )}
                        {field.type === 'date' && (
                          <input
                            type="date"
                            disabled
                            className="w-full px-3 py-2 border border-gray-300 bg-white"
                          />
                        )}
                        {field.type === 'dropdown' && (
                          <select disabled className="w-full px-3 py-2 border border-gray-300 bg-white">
                            <option>Select an option</option>
                          </select>
                        )}
                        {field.type === 'checkbox' && (
                          <div className="flex items-center gap-2">
                            <input type="checkbox" disabled className="w-4 h-4" />
                            <span className="text-sm text-gray-600">Checkbox option</span>
                          </div>
                        )}
                        {field.type === 'radio' && (
                          <div className="flex items-center gap-2">
                            <input type="radio" disabled className="w-4 h-4" />
                            <span className="text-sm text-gray-600">Radio option</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {selectedForm.fields.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-8">No fields in this form</p>
                )}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowViewModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Excel Preview Modal */}
      {showPreviewModal && excelPreviewData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-800">Excel Preview</h2>
                <p className="text-sm text-gray-600 mt-1">{excelPreviewData.fileName}</p>
              </div>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-6">
              <div className="overflow-x-auto">
                <table className="w-full border border-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {excelPreviewData.headers.map((header, index) => (
                        <th key={index} className="px-4 py-2 text-left text-sm font-semibold text-gray-700 border border-gray-200">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {excelPreviewData.rows.map((row, rowIndex) => (
                      <tr key={rowIndex} className="hover:bg-gray-50">
                        {row.map((cell, cellIndex) => (
                          <td key={cellIndex} className="px-4 py-2 text-sm text-gray-700 border border-gray-200">
                            {cell || '-'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowPreviewModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  setShowAssignModal(true);
                }}
                className="px-4 py-2 bg-[#3d82c4] text-white font-medium hover:bg-[#2d6fad] transition-colors"
              >
                Continue to Assign
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Form Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-800">Assign Form</h2>
              <p className="text-sm text-gray-600 mt-1">Select districts and roles to assign this form</p>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* District Selection */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Select Districts</h3>
                <div className="grid grid-cols-2 gap-3">
                  {districts.map((district) => (
                    <label key={district} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedDistricts.includes(district)}
                        onChange={() => handleDistrictChange(district)}
                        className="w-4 h-4 text-[#3d82c4] border-gray-300 focus:ring-[#3d82c4]"
                      />
                      <span className="text-sm text-gray-700">{district}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Role Selection */}
              {selectedDistricts.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Select Roles</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {roles.map((role) => (
                      <label key={role} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedRoles.includes(role)}
                          onChange={() => handleRoleToggle(role)}
                          className="w-4 h-4 text-[#3d82c4] border-gray-300 focus:ring-[#3d82c4]"
                        />
                        <span className="text-sm text-gray-700">{role}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Summary */}
              {selectedDistricts.length > 0 && selectedRoles.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 p-4">
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">Assignment Summary</h4>
                  <p className="text-sm text-gray-700">
                    <strong>Districts:</strong> {selectedDistricts.join(', ')}
                  </p>
                  <p className="text-sm text-gray-700 mt-1">
                    <strong>Roles:</strong> {selectedRoles.join(', ')}
                  </p>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedDistricts([]);
                  setSelectedRoles([]);
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedDistricts([]);
                  setSelectedRoles([]);
                  setEditingFormId(null);
                }}
                className="px-4 py-2 bg-[#3d82c4] text-white font-medium hover:bg-[#2d6fad] transition-colors"
                disabled={selectedDistricts.length === 0 || selectedRoles.length === 0}
              >
                Assign Form
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomizableForms;